import { useEffect, useMemo, useState } from 'react';
import { useApp } from '../context/AppContext';
import { PageHeader } from '../components/ui/PageHeader';
import { Button } from '../components/ui/Button';
import { ExportMenu } from '../components/ui/ExportMenu';
import { Card, CardBody } from '../components/ui/Card';
import { StatCard } from '../components/ui/StatCard';
import { DataTable } from '../components/ui/DataTable';
import { EmptyState } from '../components/ui/EmptyState';
import { Sheet } from '../components/ui/Sheet';
import { Input, Select } from '../components/ui/Input';
import { SkeletonCard } from '../components/ui/Skeleton';
import DatePicker from '../components/DatePicker';
import { SegmentedControl } from '../components/ui/SegmentedControl';
import { MonthlyReportTable } from '../components/MonthlyReportTable';
import { buildDayConsolidatedRows, buildDayHistoryRows, DayConsolidatedTable, DayHistoryTable } from '../components/DayHistoryTable';
import { PasswordChange } from '../components/PasswordChange';
import { HeadSettings } from './HeadSettings';
import { MoreMenu } from '../layout/MoreMenu';
import { BackButton } from '../components/ui/BackButton';
import { secondaryTabsByRole } from '../layout/roleTabs';
import { Plus, Trash2, Shuffle, X, KeyRound, Pencil } from 'lucide-react';
import { ResetPasswordSheet } from '../components/ResetPasswordSheet';
import { StrengthStatusPanel } from '../components/StrengthStatusPanel';
import { NutritionReportPanel } from '../components/NutritionReportPanel';
import { CLASS_LEVEL_OPTIONS, buildDivisionName } from '../constants/classLevels';
import { sortByDivisionName } from '../utils/sortDivisionNames';
import { getClassCapacityTotal, getClassEnrolledTotal } from '../utils/classCapacityDisplay';
import { getIstDateIso } from '../utils/istTime';
import { toDateIso, normalizeDateIso } from '../utils/dateIso';

const LIST_SEARCH_THRESHOLD = 10;

function matchesSearch(text, query) {
  return !query || String(text ?? '').toLowerCase().includes(query);
}

export function HeadDashboard({ activeTab, onNavigate }) {
  const app = useApp();
  const {
    labels,
    strengths,
    classes,
    teachers,
    historicalStrengths,
    monthlyReport,
    selectedMonth,
    setSelectedMonth,
    selectedDate,
    setSelectedDate,
    fetchReport,
    fetchClasses,
    fetchHistoricalReport,
    fetchMonthlyReport,
    exportData,
    exportMonthlyReport,
    exportDayHistoryData,
    isLoading,
    newClass,
    setNewClass,
    addClass,
    newTeacher,
    setNewTeacher,
    addTeacher,
    deleteClass,
    deleteTeacher,
    resetTeacherPassword,
    updateTeacherName,
    headUpdateStrength,
    updateClassCapacity,
    deleteHistoricalReport,
    strengthStatus,
    fetchStrengthStatus,
    isLoadingStrengthStatus,
    useMalayalam,
    token,
    assignTeacherToClass,
    unassignTeacherFromClass,
    userInfo,
    subscriptionWritesBlocked,
    nutritionReport,
    nutritionHistoricalDates,
    nutritionHistoricalReport,
    selectedNutritionDate,
    setSelectedNutritionDate,
    fetchNutritionReport,
    fetchNutritionHistoricalDates,
    fetchNutritionHistoricalReport,
    deleteNutritionHistorical,
    exportNutritionData,
    exportNutritionAll,
  } = app;

  const schoolCode = userInfo?.username || '';

  const [sheet, setSheet] = useState(null);
  const [exportOpen, setExportOpen] = useState(false);
  const [monthlyExportOpen, setMonthlyExportOpen] = useState(false);
  const [dayExportOpen, setDayExportOpen] = useState(false);
  const [historyMode, setHistoryMode] = useState('day');
  const [reportViewMode, setReportViewMode] = useState('consolidated');
  const [assignTarget, setAssignTarget] = useState(null);
  const [addClassId, setAddClassId] = useState('');
  const [editMaxClass, setEditMaxClass] = useState(null);
  const [editMaxBoys, setEditMaxBoys] = useState('');
  const [editMaxGirls, setEditMaxGirls] = useState('');
  const [classSearch, setClassSearch] = useState('');
  const [teacherSearch, setTeacherSearch] = useState('');
  const [resetPasswordTeacher, setResetPasswordTeacher] = useState(null);
  const [editNameTeacher, setEditNameTeacher] = useState(null);
  const [editNameValue, setEditNameValue] = useState('');
  const [recordStrengthClass, setRecordStrengthClass] = useState(null);
  const [recordStrengthBoys, setRecordStrengthBoys] = useState('');
  const [recordStrengthGirls, setRecordStrengthGirls] = useState('');
  const [historyDataMode, setHistoryDataMode] = useState('nm');
  const [nutritionReportDate, setNutritionReportDate] = useState(() => getIstDateIso());

  const nutritionAvailableDates = useMemo(() => {
    const set = new Set(
      (nutritionHistoricalDates || []).map((d) => normalizeDateIso(d)).filter(Boolean)
    );
    const reportDate = normalizeDateIso(nutritionReport?.date);
    if (reportDate) set.add(reportDate);
    const historyDate = normalizeDateIso(nutritionHistoricalReport?.date);
    if (historyDate) set.add(historyDate);
    return Array.from(set).sort((a, b) => b.localeCompare(a));
  }, [nutritionHistoricalDates, nutritionReport?.date, nutritionHistoricalReport?.date]);

  useEffect(() => {
    if (activeTab === 'report') {
      fetchStrengthStatus();
    }
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (activeTab !== 'nutrition' && !(activeTab === 'history' && historyDataMode === 'nutrition')) return;
    fetchNutritionHistoricalDates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, historyDataMode]);

  useEffect(() => {
    if (!nutritionAvailableDates.length) return;
    const today = getIstDateIso();
    setNutritionReportDate((prev) => {
      const p = normalizeDateIso(prev);
      if (p && nutritionAvailableDates.includes(p)) return p;
      if (nutritionAvailableDates.includes(today)) return today;
      return nutritionAvailableDates[0];
    });
  }, [nutritionAvailableDates]);

  useEffect(() => {
    if (activeTab !== 'nutrition' || !nutritionReportDate) return;
    fetchNutritionReport(normalizeDateIso(nutritionReportDate));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, nutritionReportDate]);

  useEffect(() => {
    if (activeTab !== 'history' || historyDataMode !== 'nutrition') return;
    const today = getIstDateIso();
    setSelectedNutritionDate((prev) => {
      const p = normalizeDateIso(prev);
      if (p && nutritionAvailableDates.includes(p)) return p;
      if (nutritionAvailableDates.includes(today)) return today;
      return nutritionAvailableDates[0] || today;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, historyDataMode, nutritionAvailableDates]);

  useEffect(() => {
    if (activeTab !== 'history' || historyDataMode !== 'nutrition' || !selectedNutritionDate) return;
    fetchNutritionHistoricalReport(normalizeDateIso(selectedNutritionDate));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, historyDataMode, selectedNutritionDate, nutritionHistoricalDates]);

  const unassignedClasses = sortByDivisionName(classes.filter((c) => !c.teacher_id));

  const filteredClasses = useMemo(() => {
    const q = classSearch.trim().toLowerCase();
    if (!q) return classes;
    return classes.filter(
      (cls) =>
        matchesSearch(cls.division_name, q) ||
        matchesSearch(cls.teacher_name, q)
    );
  }, [classes, classSearch]);

  const dayHistoryDateIso = toDateIso(selectedDate);
  const todayDateIso = useMemo(() => toDateIso(new Date()), []);

  const todayReportRows = useMemo(
    () =>
      sortByDivisionName(
        (strengths || []).map((row) => {
          const boys = parseInt(row.boys, 10) || 0;
          const girls = parseInt(row.girls, 10) || 0;
          return {
            division_name: row.division_name,
            boys,
            girls,
            total: boys + girls,
          };
        })
      ),
    [strengths]
  );

  const reportConsolidatedRows = useMemo(
    () => buildDayConsolidatedRows(todayReportRows),
    [todayReportRows]
  );

  // Refresh today's report when head opens Report again
  useEffect(() => {
    if (activeTab !== 'report') return;
    fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Reload history data when returning to History (tab/mode) without re-picking date/month
  useEffect(() => {
    if (activeTab !== 'history') return;
    if (historyMode === 'day' || historyMode === 'dayConsolidated') {
      if (selectedDate) fetchHistoricalReport(selectedDate);
      return;
    }
    if (selectedMonth) fetchMonthlyReport(selectedMonth);
    // fetch* omitted from deps — stable enough; avoids refetch loop on every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, historyMode, selectedDate, selectedMonth]);

  const dayHistoryRows = useMemo(
    () => buildDayHistoryRows(classes, historicalStrengths, dayHistoryDateIso),
    [classes, historicalStrengths, dayHistoryDateIso]
  );
  const dayConsolidatedRows = useMemo(
    () => buildDayConsolidatedRows(dayHistoryRows),
    [dayHistoryRows]
  );

  const filteredTeachers = useMemo(() => {
    const q = teacherSearch.trim().toLowerCase();
    if (!q) return teachers;
    return teachers.filter((t) => {
      const assignedClassNames = classes
        .filter((c) => String(c.teacher_id) === String(t.id))
        .map((c) => c.division_name)
        .join(', ');
      return (
        matchesSearch(t.name, q) ||
        matchesSearch(t.username, q) ||
        matchesSearch(t.class_name, q) ||
        matchesSearch(assignedClassNames, q)
      );
    });
  }, [teachers, teacherSearch, classes]);

  const classesForTeacher = (teacherId) =>
    classes.filter((c) => String(c.teacher_id) === String(teacherId));

  const classLabelForTeacher = (t) => {
    const names = classesForTeacher(t.id).map((c) => c.division_name);
    if (names.length) return names.join(', ');
    return t.class_name || labels.notAssignedToClass;
  };

  const openAssignClass = (teacher) => {
    setAssignTarget(teacher);
    setAddClassId('');
  };

  const addClassToTeacher = async () => {
    if (!assignTarget || !addClassId) return;
    const ok = await assignTeacherToClass(assignTarget.id, parseInt(addClassId, 10));
    if (ok) {
      setAddClassId('');
    }
  };

  const removeClassFromTeacher = async (classId) => {
    if (!assignTarget) return;
    await unassignTeacherFromClass(assignTarget.id, classId);
  };

  const openEditMax = (cls) => {
    setEditMaxClass(cls);
    setEditMaxBoys(cls.max_boys != null && cls.max_boys !== '' ? String(cls.max_boys) : '');
    setEditMaxGirls(cls.max_girls != null && cls.max_girls !== '' ? String(cls.max_girls) : '');
  };

  const openRecordStrength = (cls) => {
    setRecordStrengthClass(cls);
    setRecordStrengthBoys(cls.boys != null && cls.boys !== '' ? String(cls.boys) : '');
    setRecordStrengthGirls(cls.girls != null && cls.girls !== '' ? String(cls.girls) : '');
  };

  const saveRecordStrength = async () => {
    if (!recordStrengthClass) return;
    const ok = await headUpdateStrength(recordStrengthClass, recordStrengthBoys, recordStrengthGirls);
    if (ok) setRecordStrengthClass(null);
  };

  const saveEditMax = async () => {
    if (!editMaxClass) return;
    const boys = parseInt(editMaxBoys, 10);
    const girls = parseInt(editMaxGirls, 10);
    if (editMaxBoys === '' || editMaxGirls === '' || Number.isNaN(boys) || Number.isNaN(girls) || boys < 0 || girls < 0) {
      return;
    }
    if (boys === 0 && girls === 0) return;
    const ok = await updateClassCapacity(editMaxClass.id, { max_boys: boys, max_girls: girls });
    if (ok) setEditMaxClass(null);
  };

  const formatClassCaps = (cls) => {
    const maxBoys = parseInt(cls.max_boys, 10) || 0;
    const maxGirls = parseInt(cls.max_girls, 10) || 0;
    if (maxBoys > 0 || maxGirls > 0) {
      return `${labels.maxBoys}: ${maxBoys || '—'} · ${labels.maxGirls}: ${maxGirls || '—'}`;
    }
    if (cls.max_students) {
      return `${labels.maxStudents}: ${cls.max_students}`;
    }
    return `${labels.maxStudents}: —`;
  };

  if (activeTab === 'more') {
    return <MoreMenu role="head" labels={labels} onSelect={onNavigate} />;
  }

  const showMobileBack = secondaryTabsByRole.head.includes(activeTab);

  const wrapWithBack = (content) => (
    <div>
      {showMobileBack && (
        <BackButton onClick={() => onNavigate('more')} label={labels.back} />
      )}
      {content}
    </div>
  );

  if (activeTab === 'account') {
    return wrapWithBack(<PasswordChange token={token} labels={labels} />);
  }

  if (activeTab === 'settings') {
    return wrapWithBack(<HeadSettings />);
  }

  if (activeTab === 'nutrition') {
    return wrapWithBack(
      <NutritionReportPanel
        labels={labels}
        report={nutritionReport}
        isLoading={isLoading.nutritionReport}
        selectedDate={nutritionReportDate}
        onDateChange={setNutritionReportDate}
        availableDates={nutritionAvailableDates}
        onRefresh={() => {
          const date = normalizeDateIso(nutritionReportDate) || getIstDateIso();
          fetchNutritionHistoricalDates();
          fetchNutritionReport(date);
        }}
        exportNutritionData={exportNutritionData}
        exportNutritionAll={exportNutritionAll}
      />
    );
  }

  const totalBoys = strengths.reduce((s, r) => s + (parseInt(r.boys, 10) || 0), 0);
  const totalGirls = strengths.reduce((s, r) => s + (parseInt(r.girls, 10) || 0), 0);

  const reportColumns = [
    { key: 'division', label: labels.division, render: (r) => r.division_name },
    { key: 'boys', label: labels.boys, render: (r) => r.boys ?? 0 },
    { key: 'girls', label: labels.girls, render: (r) => r.girls ?? 0 },
    {
      key: 'total',
      label: labels.totalStudents,
      render: (r) => (parseInt(r.boys, 10) || 0) + (parseInt(r.girls, 10) || 0),
    },
  ];

  if (activeTab === 'report') {
    return (
      <div className="space-y-4">
        <PageHeader
          title={labels.reportTitle}
          actions={
            <>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  fetchReport();
                  fetchStrengthStatus();
                }}
                isLoading={isLoading.report || isLoadingStrengthStatus}
              >
                {labels.refresh}
              </Button>
              <ExportMenu
                open={exportOpen}
                onOpenChange={setExportOpen}
                labels={labels}
                align="right"
                placement="down"
                disabled={!strengths.length}
                onExport={(format) => {
                  if (reportViewMode === 'consolidated') {
                    exportDayHistoryData({
                      rows: reportConsolidatedRows,
                      fileType: format,
                      dateIso: todayDateIso,
                      isConsolidated: true,
                    });
                  } else {
                    exportData(strengths, format);
                  }
                }}
              />
            </>
          }
        />
        <Card>
          <CardBody className="space-y-4">
            <SegmentedControl
              options={[
                { value: 'consolidated', label: labels.reportTodayConsolidated },
                { value: 'detail', label: labels.reportViewByDivision },
              ]}
              value={reportViewMode}
              onChange={setReportViewMode}
            />
          </CardBody>
        </Card>
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <StatCard label={labels.boys} value={totalBoys} />
          <StatCard label={labels.girls} value={totalGirls} />
          <StatCard label={labels.totalStudents} value={totalBoys + totalGirls} />
          <StatCard label={labels.classesTitle} value={strengths.length} />
        </div>
        {isLoading.report ? (
          <SkeletonCard />
        ) : !strengths.length ? (
          <EmptyState title={labels.noData} message={labels.refresh} />
        ) : reportViewMode === 'consolidated' ? (
          <DayConsolidatedTable rows={reportConsolidatedRows} labels={labels} />
        ) : (
          <DataTable
            columns={reportColumns}
            data={strengths}
            emptyMessage={labels.noData}
            renderMobileCard={(row) => (
              <Card>
                <CardBody className="flex justify-between">
                  <div>
                    <p className="font-medium">{row.division_name}</p>
                    <p className="text-sm text-muted">
                      {labels.boys}: {row.boys} · {labels.girls}: {row.girls}
                    </p>
                  </div>
                  <p className="font-semibold text-text">
                    {(parseInt(row.boys, 10) || 0) + (parseInt(row.girls, 10) || 0)}
                  </p>
                </CardBody>
              </Card>
            )}
          />
        )}
        <StrengthStatusPanel
          labels={labels}
          strengthStatus={strengthStatus}
          isLoading={isLoadingStrengthStatus}
          schoolName={userInfo?.name || ''}
          useMalayalam={useMalayalam}
        />
      </div>
    );
  }

  if (activeTab === 'classes') {
    return (
      <div className="space-y-4">
        <PageHeader
          title={labels.classesTitle}
          actions={
            <Button size="sm" onClick={() => setSheet('class')} disabled={subscriptionWritesBlocked}>
              <Plus className="h-4 w-4" />
              {labels.addClass}
            </Button>
          }
        />
        {isLoading.classes ? (
          <SkeletonCard />
        ) : classes.length === 0 ? (
          <EmptyState title={labels.noData} actionLabel={labels.addClass} onAction={() => setSheet('class')} />
        ) : (
          <div className="space-y-3">
            {classes.length > LIST_SEARCH_THRESHOLD && (
              <Input
                placeholder={labels.search}
                value={classSearch}
                onChange={(e) => setClassSearch(e.target.value)}
                className="max-w-md"
              />
            )}
            {filteredClasses.length === 0 ? (
              <EmptyState title={labels.searchNoResults} />
            ) : (
              filteredClasses.map((cls) => (
                <Card key={cls.id}>
                  <CardBody className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <button
                      type="button"
                      className="min-w-0 flex-1 text-left rounded-lg -m-1 p-1 hover:bg-surface/80 transition-colors"
                      onClick={() => openRecordStrength(cls)}
                      disabled={subscriptionWritesBlocked}
                    >
                      <p className="font-medium text-text">{cls.division_name}</p>
                      <p className="text-sm text-muted">{cls.teacher_name || labels.notAssignedToClass}</p>
                      <p className="text-xs text-muted mt-1">
                        {formatClassCaps(cls)} · {labels.classCapacityTotal}: {getClassCapacityTotal(cls)} ·{' '}
                        {labels.classStudentsTotal}: {getClassEnrolledTotal(cls)}
                      </p>
                    </button>
                    <div className="flex gap-2 shrink-0">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => openRecordStrength(cls)}
                        disabled={subscriptionWritesBlocked}
                      >
                        {labels.headRecordStrength}
                      </Button>
                      <Button variant="secondary" size="sm" onClick={() => openEditMax(cls)}>
                        {labels.editMax}
                      </Button>
                      <Button variant="danger" size="sm" onClick={() => deleteClass(cls.id, cls.division_name)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardBody>
                </Card>
              ))
            )}
          </div>
        )}
        <Sheet open={sheet === 'class'} onClose={() => setSheet(null)} title={labels.addClass}>
          <div className="space-y-4">
            <Select
              label={labels.classNumber}
              value={newClass.class_number}
              onChange={(e) => setNewClass({ ...newClass, class_number: e.target.value })}
            >
              <option value="">{labels.selectClassNumber}</option>
              {CLASS_LEVEL_OPTIONS.map(({ value, label }) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Select>
            <Input
              label={labels.classDivision}
              value={newClass.division_letter}
              onChange={(e) => {
                const v = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
                setNewClass({ ...newClass, division_letter: v });
              }}
              placeholder={labels.classDivisionPlaceholder}
              autoComplete="off"
            />
            {newClass.class_number && newClass.division_letter && (
              <p className="text-sm font-medium text-primary">
                {labels.classNamePreview}:{' '}
                {buildDivisionName(newClass.class_number, newClass.division_letter)}
              </p>
            )}
            <div className="grid grid-cols-2 gap-3">
              <Input
                label={labels.classMaxBoys}
                type="number"
                min={0}
                inputMode="numeric"
                value={newClass.max_boys}
                onChange={(e) => setNewClass({ ...newClass, max_boys: e.target.value })}
              />
              <Input
                label={labels.classMaxGirls}
                type="number"
                min={0}
                inputMode="numeric"
                value={newClass.max_girls}
                onChange={(e) => setNewClass({ ...newClass, max_girls: e.target.value })}
              />
            </div>
            <p className="text-xs text-muted">{labels.classCapsHint}</p>
            <Button
              className="w-full"
              onClick={async () => {
                await addClass();
                setSheet(null);
                fetchClasses();
              }}
              isLoading={isLoading.addClass}
              disabled={subscriptionWritesBlocked}
            >
              {labels.save}
            </Button>
          </div>
        </Sheet>
        <Sheet
          open={!!recordStrengthClass}
          onClose={() => setRecordStrengthClass(null)}
          title={labels.headRecordStrengthTitle}
        >
          {recordStrengthClass && (
            <div className="space-y-4">
              <p className="text-sm text-muted">
                {labels.division}:{' '}
                <span className="font-medium text-text">{recordStrengthClass.division_name}</span>
                {recordStrengthClass.teacher_name ? (
                  <span className="block mt-1">{recordStrengthClass.teacher_name}</span>
                ) : null}
              </p>
              <p className="text-xs text-muted">{labels.headRecordStrengthHint}</p>
              <p className="text-sm font-medium text-text">{getIstDateIso()}</p>
              <div className="grid grid-cols-2 gap-3">
                <Input
                  label={labels.boys}
                  type="number"
                  min={0}
                  inputMode="numeric"
                  value={recordStrengthBoys}
                  onChange={(e) => setRecordStrengthBoys(e.target.value)}
                />
                <Input
                  label={labels.girls}
                  type="number"
                  min={0}
                  inputMode="numeric"
                  value={recordStrengthGirls}
                  onChange={(e) => setRecordStrengthGirls(e.target.value)}
                />
              </div>
              <p className="text-xs text-muted">{formatClassCaps(recordStrengthClass)}</p>
              <Button
                className="w-full"
                onClick={saveRecordStrength}
                isLoading={isLoading.headUpdateStrength}
                disabled={
                  subscriptionWritesBlocked ||
                  recordStrengthBoys.trim() === '' ||
                  recordStrengthGirls.trim() === ''
                }
              >
                {labels.save}
              </Button>
            </div>
          )}
        </Sheet>
        <Sheet
          open={!!editMaxClass}
          onClose={() => setEditMaxClass(null)}
          title={labels.editMaxTitle}
        >
          {editMaxClass && (
            <div className="space-y-4">
              <p className="text-sm text-muted">
                {labels.division}: <span className="font-medium text-text">{editMaxClass.division_name}</span>
              </p>
              <div className="grid grid-cols-2 gap-3">
                <Input
                  label={labels.classMaxBoys}
                  type="number"
                  min={0}
                  inputMode="numeric"
                  value={editMaxBoys}
                  onChange={(e) => setEditMaxBoys(e.target.value)}
                  placeholder={labels.editMaxPlaceholder}
                />
                <Input
                  label={labels.classMaxGirls}
                  type="number"
                  min={0}
                  inputMode="numeric"
                  value={editMaxGirls}
                  onChange={(e) => setEditMaxGirls(e.target.value)}
                  placeholder={labels.editMaxPlaceholder}
                />
              </div>
              <p className="text-xs text-muted">{labels.editMaxHint}</p>
              <div className="flex gap-2">
                <Button variant="secondary" className="flex-1" onClick={() => setEditMaxClass(null)}>
                  {labels.cancel}
                </Button>
                <Button
                  className="flex-1"
                  onClick={saveEditMax}
                  isLoading={isLoading.updateCapacity}
                  disabled={
                    editMaxBoys.trim() === '' ||
                    editMaxGirls.trim() === '' ||
                    Number.isNaN(Number(editMaxBoys)) ||
                    Number.isNaN(Number(editMaxGirls)) ||
                    Number(editMaxBoys) < 0 ||
                    Number(editMaxGirls) < 0 ||
                    (Number(editMaxBoys) === 0 && Number(editMaxGirls) === 0)
                  }
                >
                  {labels.save}
                </Button>
              </div>
            </div>
          )}
        </Sheet>
      </div>
    );
  }

  if (activeTab === 'teachers') {
    return (
      <div className="space-y-4">
        <PageHeader
          title={labels.teachersTitle}
          actions={
            <Button size="sm" onClick={() => setSheet('teacher')} disabled={subscriptionWritesBlocked}>
              <Plus className="h-4 w-4" />
              {labels.addTeacher}
            </Button>
          }
        />
        {isLoading.teachers ? (
          <SkeletonCard />
        ) : teachers.length === 0 ? (
          <EmptyState title={labels.noData} actionLabel={labels.addTeacher} onAction={() => setSheet('teacher')} />
        ) : (
          <div className="space-y-3">
            {teachers.length > LIST_SEARCH_THRESHOLD && (
              <Input
                placeholder={labels.search}
                value={teacherSearch}
                onChange={(e) => setTeacherSearch(e.target.value)}
                className="max-w-md"
              />
            )}
            {filteredTeachers.length === 0 ? (
              <EmptyState title={labels.searchNoResults} />
            ) : (
              filteredTeachers.map((t) => (
                <Card key={t.id}>
                  <CardBody className="flex justify-between items-center">
                    <div>
                      <p className="font-medium">{t.name}</p>
                      <p className="text-sm text-muted">@{t.username}</p>
                      <p className="text-sm text-muted mt-0.5">
                        {labels.teacherClass}: {classLabelForTeacher(t)}
                      </p>
                    </div>
                    <div className="flex flex-wrap justify-end gap-1">
                      <Button
                        variant="secondary"
                        size="sm"
                        aria-label={labels.editTeacherName}
                        onClick={() => {
                          setEditNameTeacher(t);
                          setEditNameValue(t.name || '');
                        }}
                        disabled={subscriptionWritesBlocked}
                      >
                        <Pencil className="h-4 w-4" />
                        <span className="hidden sm:inline">{labels.editTeacherName}</span>
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        aria-label={labels.resetPassword}
                        onClick={() => setResetPasswordTeacher(t)}
                        disabled={subscriptionWritesBlocked}
                      >
                        <KeyRound className="h-4 w-4" />
                        <span className="hidden sm:inline">{labels.resetPassword}</span>
                      </Button>
                      <Button variant="secondary" size="sm" onClick={() => openAssignClass(t)}>
                        <Shuffle className="h-4 w-4" />
                        <span className="hidden sm:inline">{labels.changeClass}</span>
                      </Button>
                      <Button variant="danger" size="sm" onClick={() => deleteTeacher(t.id, t.name)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardBody>
                </Card>
              ))
            )}
          </div>
        )}
        <Sheet
          open={!!assignTarget}
          onClose={() => setAssignTarget(null)}
          title={labels.assignClassTitle}
        >
          {assignTarget && (
            <div className="space-y-4">
              <p className="text-sm text-muted">
                {assignTarget.name} <span className="text-muted">(@{assignTarget.username})</span>
              </p>
              <div>
                <p className="text-sm font-medium text-text mb-2">{labels.currentClasses}</p>
                {classesForTeacher(assignTarget.id).length === 0 ? (
                  <p className="text-sm text-muted">{labels.notAssignedToClass}</p>
                ) : (
                  <ul className="space-y-2">
                    {classesForTeacher(assignTarget.id).map((c) => (
                      <li
                        key={c.id}
                        className="flex items-center justify-between rounded-lg border border-border px-3 py-2"
                      >
                        <span className="font-medium">{c.division_name}</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeClassFromTeacher(c.id)}
                          isLoading={isLoading.assignTeacher}
                          aria-label={labels.removeClass}
                        >
                          <X className="h-4 w-4" />
                          <span className="sr-only">{labels.removeClass}</span>
                        </Button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              {unassignedClasses.length > 0 ? (
                <>
                  <Select
                    label={labels.addAnotherClass}
                    value={addClassId}
                    onChange={(e) => setAddClassId(e.target.value)}
                  >
                    <option value="">{labels.selectUnassignedClass}</option>
                    {unassignedClasses.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.division_name}
                      </option>
                    ))}
                  </Select>
                  <Button
                    className="w-full"
                    onClick={addClassToTeacher}
                    disabled={!addClassId || subscriptionWritesBlocked}
                    isLoading={isLoading.assignTeacher}
                  >
                    {labels.save}
                  </Button>
                </>
              ) : (
                <p className="text-sm text-warning">{labels.noUnassignedClasses}</p>
              )}
            </div>
          )}
        </Sheet>
        <Sheet
          open={!!editNameTeacher}
          onClose={() => setEditNameTeacher(null)}
          title={labels.editTeacherNameTitle}
        >
          {editNameTeacher && (
            <div className="space-y-4">
              <p className="text-sm text-muted">
                @{editNameTeacher.username}
              </p>
              <p className="text-xs text-muted">{labels.editTeacherNameHint}</p>
              <Input
                label={labels.name}
                value={editNameValue}
                onChange={(e) => setEditNameValue(e.target.value)}
                autoFocus
              />
              <Button
                className="w-full"
                disabled={!editNameValue.trim() || subscriptionWritesBlocked}
                isLoading={isLoading.updateTeacherName}
                onClick={async () => {
                  const ok = await updateTeacherName(editNameTeacher.id, editNameValue);
                  if (ok) setEditNameTeacher(null);
                }}
              >
                {labels.save}
              </Button>
            </div>
          )}
        </Sheet>
        <ResetPasswordSheet
          open={!!resetPasswordTeacher}
          onClose={() => setResetPasswordTeacher(null)}
          title={labels.resetPasswordTitleTeacher}
          subtitle={
            resetPasswordTeacher
              ? `${resetPasswordTeacher.name} (@${resetPasswordTeacher.username})`
              : ''
          }
          labels={labels}
          isLoading={isLoading.resetTeacherPassword}
          onSubmit={(newPassword) =>
            resetPasswordTeacher
              ? resetTeacherPassword(resetPasswordTeacher.id, newPassword)
              : Promise.resolve(false)
          }
        />
        <Sheet open={sheet === 'teacher'} onClose={() => setSheet(null)} title={labels.addTeacher}>
          <div className="space-y-4">
            <Input label={labels.name} value={newTeacher.name} onChange={(e) => setNewTeacher({ ...newTeacher, name: e.target.value })} />
            <div>
              <label className="mb-1.5 block text-sm font-medium text-text">{labels.loginSuffix}</label>
              <div className="flex overflow-hidden rounded-lg border border-border bg-card focus-within:ring-2 focus-within:ring-primary/30">
                <span className="shrink-0 border-r border-border bg-surface px-3 py-2.5 text-sm font-medium text-muted">
                  {schoolCode}_
                </span>
                <input
                  type="text"
                  className="min-w-0 flex-1 bg-transparent px-3 py-2.5 text-sm text-text outline-none"
                  value={newTeacher.login_suffix}
                  onChange={(e) => setNewTeacher({ ...newTeacher, login_suffix: e.target.value })}
                  placeholder={labels.loginSuffixPlaceholder}
                  autoComplete="off"
                />
              </div>
              <p className="mt-1.5 text-xs text-muted">{labels.loginSuffixHint}</p>
              {newTeacher.login_suffix.trim() && (
                <p className="mt-1 text-sm font-medium text-primary">
                  {labels.loginIdPreview}: {schoolCode}_{newTeacher.login_suffix.trim()}
                </p>
              )}
            </div>
            <Input label={labels.password} type="password" value={newTeacher.password} onChange={(e) => setNewTeacher({ ...newTeacher, password: e.target.value })} />
            <Select
              label={labels.selectUnassignedClass}
              value={newTeacher.class_id}
              onChange={(e) => {
                const classId = e.target.value;
                const cls = unassignedClasses.find((c) => String(c.id) === String(classId));
                setNewTeacher((prev) => ({
                  ...prev,
                  class_id: classId,
                  login_suffix:
                    prev.login_suffix.trim() || (cls?.division_name ?? prev.login_suffix),
                }));
              }}
            >
              <option value="">{labels.selectClass}</option>
              {unassignedClasses.map((c) => (
                <option key={c.id} value={c.id}>{c.division_name}</option>
              ))}
            </Select>
            {unassignedClasses.length === 0 && (
              <p className="text-sm text-warning">{labels.noUnassignedClasses}</p>
            )}
            <Button
              className="w-full"
              onClick={async () => {
                await addTeacher();
                setSheet(null);
              }}
              isLoading={isLoading.addTeacher}
              disabled={unassignedClasses.length === 0 || subscriptionWritesBlocked}
            >
              {labels.save}
            </Button>
          </div>
        </Sheet>
      </div>
    );
  }

  if (activeTab === 'history') {
    return wrapWithBack(
      <div className="space-y-4">
        <PageHeader title={labels.historicalReportsTitle} />
        <SegmentedControl
          options={[
            { value: 'nm', label: labels.historyModeNm },
            { value: 'nutrition', label: labels.historyModeNutrition },
          ]}
          value={historyDataMode}
          onChange={setHistoryDataMode}
        />

        {historyDataMode === 'nutrition' ? (
          <NutritionReportPanel
            labels={labels}
            report={nutritionHistoricalReport}
            isLoading={isLoading.nutritionHistorical}
            selectedDate={selectedNutritionDate}
            onDateChange={setSelectedNutritionDate}
            availableDates={nutritionAvailableDates}
            exportNutritionData={exportNutritionData}
            exportNutritionAll={exportNutritionAll}
            showDelete
            deleteLoading={isLoading.deleteNutritionHistorical}
            onDeleteRecord={(id) => deleteNutritionHistorical(id, selectedNutritionDate)}
          />
        ) : (
          <>
        <Card className="overflow-visible">
          <CardBody className="space-y-4 overflow-visible">
            <SegmentedControl
              options={[
                { value: 'day', label: labels.reportModeDay },
                { value: 'dayConsolidated', label: labels.reportModeDayConsolidated },
                { value: 'month', label: labels.reportModeMonth },
              ]}
              value={historyMode}
              onChange={(mode) => {
                setHistoryMode(mode);
                setMonthlyExportOpen(false);
              }}
            />
            {historyMode === 'day' || historyMode === 'dayConsolidated' ? (
              <div className="space-y-3">
                <label className="mb-1.5 block text-sm font-medium">{labels.selectDatePrompt}</label>
                <DatePicker
                  selectedDate={selectedDate}
                  onDateChange={setSelectedDate}
                />
                <div className="relative z-20 flex flex-wrap items-center gap-2">
                  <ExportMenu
                    open={dayExportOpen}
                    onOpenChange={setDayExportOpen}
                    labels={labels}
                    align="left"
                    placement="up"
                    disabled={!selectedDate || classes.length === 0}
                    onExport={(format) =>
                      exportDayHistoryData({
                        rows: historyMode === 'dayConsolidated' ? dayConsolidatedRows : dayHistoryRows,
                        fileType: format,
                        dateIso: dayHistoryDateIso,
                        isConsolidated: historyMode === 'dayConsolidated',
                      })
                    }
                  />
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div>
                  <label className="mb-1.5 block text-sm font-medium">{labels.selectMonth}</label>
                  <input
                    type="month"
                    className="w-full min-h-touch rounded-lg border border-border bg-input px-3 py-2.5 text-text focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(e.target.value)}
                  />
                </div>
                <div className="relative z-20 flex flex-wrap items-center gap-2">
                  <Button
                    size="sm"
                    onClick={() => fetchMonthlyReport(selectedMonth)}
                    isLoading={isLoading.monthlyReport}
                  >
                    {labels.viewReport}
                  </Button>
                  <ExportMenu
                    open={monthlyExportOpen}
                    onOpenChange={setMonthlyExportOpen}
                    labels={labels}
                    align="left"
                    placement="up"
                    disabled={!monthlyReport?.rows?.length || !monthlyReport?.dates?.length}
                    onExport={(format) => exportMonthlyReport(format)}
                  />
                </div>
              </div>
            )}
          </CardBody>
        </Card>
        {historyMode === 'day' ? (
          isLoading.historicalReport ? (
            <SkeletonCard />
          ) : !selectedDate ? (
            <EmptyState title={labels.noData} message={labels.selectDatePrompt} />
          ) : classes.length === 0 ? (
            <EmptyState title={labels.noData} message={labels.addClass} />
          ) : (
            <DayHistoryTable
              rows={dayHistoryRows}
              labels={labels}
              onDelete={deleteHistoricalReport}
              deleteLoading={isLoading.deleteHistorical}
            />
          )
        ) : historyMode === 'dayConsolidated' ? (
          isLoading.historicalReport ? (
            <SkeletonCard />
          ) : !selectedDate ? (
            <EmptyState title={labels.noData} message={labels.selectDatePrompt} />
          ) : classes.length === 0 ? (
            <EmptyState title={labels.noData} message={labels.addClass} />
          ) : (
            <DayConsolidatedTable rows={dayConsolidatedRows} labels={labels} />
          )
        ) : isLoading.monthlyReport ? (
          <SkeletonCard />
        ) : !monthlyReport ? (
          <EmptyState title={labels.noData} message={labels.selectMonth} />
        ) : monthlyReport.dates?.length === 0 ? (
          <EmptyState title={labels.noMonthlyData} message={labels.selectMonth} />
        ) : (
          <MonthlyReportTable report={monthlyReport} labels={labels} useMalayalam={app.useMalayalam} />
        )}
          </>
        )}
      </div>
    );
  }

  return null;
}
